export const upsertMutation = (key, value) => ({ type: 'upsert', key, value });
export const deleteMutation = (key) => ({ type: 'delete', key });

const findSetting = (settings = [], key) => settings.find((setting) => setting.name === key);

const createSimpleFeature = ({
    id,
    label,
    description,
    scope,
    key,
    defaultState = false,
    enableValue = '1',
    disableValue = '0',
}) => ({
    id,
    label,
    description,
    scope,
    type: 'simple',
    relatedKeys: [key],
    resolve: (settings) => {
        const match = findSetting(settings, key);
        if (!match || match.value == null) return defaultState;
        return match.value === enableValue;
    },
    mutations: {
        true: [upsertMutation(key, enableValue)],
        false: [upsertMutation(key, disableValue)],
    },
});

const createDefaultTrueFeature = ({
    id,
    label,
    description,
    scope,
    key,
    disableValue = '0',
    enableValue = '1',
}) => ({
    id,
    label,
    description,
    scope,
    type: 'defaultTrue',
    relatedKeys: [key],
    resolve: (settings) => {
        const match = findSetting(settings, key);
        if (!match || match.value == null) return true;
        return match.value !== disableValue;
    },
    mutations: {
        true: [upsertMutation(key, enableValue)],
        false: [upsertMutation(key, disableValue)],
    },
});

const createInverseFeature = ({
    id,
    label,
    description,
    scope,
    key,
    hideValue = '1',
    defaultState = true,
}) => ({
    id,
    label,
    description,
    scope,
    type: 'inverse',
    relatedKeys: [key],
    resolve: (settings) => {
        const match = findSetting(settings, key);
        if (!match || match.value == null) return defaultState;
        return match.value !== hideValue;
    },
    mutations: {
        true: [deleteMutation(key)],
        false: [upsertMutation(key, hideValue)],
    },
});

const createMenuFeature = ({
    id,
    label,
    description,
    scope,
    showKey,
    hideKey,
    defaultState = true,
}) => ({
    id,
    label,
    description,
    scope,
    type: 'menu',
    relatedKeys: [showKey, hideKey].filter(Boolean),
    resolve: (settings) => {
        const hide = hideKey ? findSetting(settings, hideKey) : null;
        if (hide && hide.value === '1') return false;
        if (showKey) {
            const show = findSetting(settings, showKey);
            if (show) return show.value !== '0';
            return false;
        }
        return defaultState;
    },
    mutations: {
        true: [
            ...(showKey ? [upsertMutation(showKey, '1')] : []),
            ...(hideKey ? [deleteMutation(hideKey)] : []),
        ],
        false: [
            ...(showKey ? [deleteMutation(showKey)] : []),
            ...(hideKey ? [upsertMutation(hideKey, '1')] : []),
        ],
    },
});

const BRANDING_CONTENT_FEATURES = [
    createDefaultTrueFeature({
        id: 'branding-training-content',
        label: 'Training subjects in LMS content',
        description: 'Show training subjects in the LMS content area.',
        scope: 'branding',
        key: 'ui.content.trainings',
    }),
    createDefaultTrueFeature({
        id: 'branding-assessment-content',
        label: 'Assessment scenarios in LMS content',
        description: 'Show risk assessment scenarios in the LMS content area.',
        scope: 'branding',
        key: 'ui.content.assessments',
    }),
    createDefaultTrueFeature({
        id: 'branding-reinforcement-materials',
        label: 'Reinforcement materials in LMS',
        description: 'Allow LMS managers to browse downloadable reinforcement materials.',
        scope: 'branding',
        key: 'ui.content.materials',
    }),
    createSimpleFeature({
        id: 'branding-questions-editor',
        label: 'Risk assessment question editor',
        description: 'Enable the questions editor in the LMS content area.',
        scope: 'branding',
        key: 'ui.questions-editor',
    }),
    createSimpleFeature({
        id: 'branding-template-standards',
        label: 'Template standards library',
        description: 'Expose the template standards tab for training automations.',
        scope: 'branding',
        key: 'ui.training.templateStandards',
    }),
];

const LEARNER_EXPERIENCE_FEATURES = [
    createSimpleFeature({
        id: 'branding-training-skip-auto-finish',
        label: 'Skip auto-finish for SCORM',
        description: 'Prevent SCORM trainings from auto-completing once requirements are met.',
        scope: 'branding',
        key: 'ui.training.skip_auto_finish',
    }),
    createSimpleFeature({
        id: 'branding-disable-fast-forward',
        label: 'Disable fast-forward in MY videos',
        description: 'Prevent learners from fast-forwarding videos in the MY portal.',
        scope: 'branding',
        key: 'ui.disable_fast_forward',
        defaultState: false,
        enableValue: '1',
        disableValue: '0',
    }),
    createSimpleFeature({
        id: 'branding-speech-over',
        label: 'Speech-over assistance',
        description: 'Allow learners to trigger text-to-speech for narrative content.',
        scope: 'branding',
        key: 'ui.speech_over',
    }),
    createSimpleFeature({
        id: 'branding-scorm-certificate',
        label: 'SCORM certificate download',
        description: 'Expose certificate downloads for SCORM content.',
        scope: 'branding',
        key: 'ui.scormCertificate',
    }),
    createSimpleFeature({
        id: 'branding-hide-language-selector',
        label: 'Hide language selector',
        description: 'Hide the language switcher in learner experiences.',
        scope: 'branding',
        key: 'ui.hideLanguageList',
        defaultState: false,
        enableValue: '1',
        disableValue: '0',
    }),
];

const NAVIGATION_FEATURES = [
    createMenuFeature({
        id: 'nav-assessment-insight',
        label: 'Assessment Insight tab',
        description: 'Show the Assessment Insight navigation tab in LMS.',
        scope: 'company',
        showKey: 'ui.menu_show_assessment-insight_tab',
        hideKey: 'ui.menu_hide_assessment-insight_tab',
        defaultState: false,
    }),
    createMenuFeature({
        id: 'nav-phishing-simulation',
        label: 'Phishing simulation tab',
        description:
            'Show the Phishing simulation tab (Microsoft Defender AST / Graph) in LMS.',
        scope: 'company',
        showKey: 'ui.menu_show_phishing-simulation_tab',
        hideKey: 'ui.menu_hide_phishing-simulation_tab',
        defaultState: false,
    }),
    createSimpleFeature({
        id: 'nav-hide-trainings-tab',
        label: 'Hide Training programs tab',
        description: 'Hide the Training programs tab in LMS navigation.',
        scope: 'company',
        key: 'ui.menu_hide_trainings_tab',
        defaultState: false,
        enableValue: '1',
        disableValue: '0',
    }),
    createSimpleFeature({
        id: 'nav-hide-assessment-tab',
        label: 'Hide Assessments tab',
        description: 'Hide the Assessments tab in LMS navigation.',
        scope: 'company',
        key: 'ui.menu_hide_assessment_tab',
        defaultState: false,
        enableValue: '1',
        disableValue: '0',
    }),
    createSimpleFeature({
        id: 'nav-hide-users-tab',
        label: 'Hide Users tab',
        description: 'Hide the Users tab in LMS navigation.',
        scope: 'company',
        key: 'ui.menu_hide_users_tab',
        defaultState: false,
        enableValue: '1',
        disableValue: '0',
    }),
    createSimpleFeature({
        id: 'nav-hide-reports-tab',
        label: 'Hide Reports tab',
        description: 'Hide the Reports tab in LMS navigation.',
        scope: 'company',
        key: 'ui.menu_hide_reports_tab',
        defaultState: false,
        enableValue: '1',
        disableValue: '0',
    }),
    createSimpleFeature({
        id: 'nav-hide-content-tab',
        label: 'Hide Content tab',
        description: 'Hide the Content tab in LMS navigation.',
        scope: 'company',
        key: 'ui.menu_hide_content_tab',
        defaultState: false,
        enableValue: '1',
        disableValue: '0',
    }),
    createSimpleFeature({
        id: 'nav-hide-help-tab',
        label: 'Hide Help tab',
        description: 'Hide the Help tab in LMS navigation.',
        scope: 'company',
        key: 'ui.menu_hide_help_tab',
        defaultState: false,
        enableValue: '1',
        disableValue: '0',
    }),
];

const TRAINING_ASSESSMENT_FEATURES = [
    createSimpleFeature({
        id: 'company-send-enrolment',
        label: 'Send enrolment emails to new employees',
        description: 'Automatically send welcome emails when employees are added to a training list.',
        scope: 'company',
        key: 'ui.sendEnrolment',
    }),
    createDefaultTrueFeature({
        id: 'company-assessment-dashboard',
        label: 'Assessment dashboard',
        description: 'Show the assessment dashboard module in LMS.',
        scope: 'company',
        key: 'ui.assessment_dashboard',
    }),
    createSimpleFeature({
        id: 'company-assessment-recommendations',
        label: 'Assessment recommendations',
        description: 'Enable assessment recommendation cards for managers.',
        scope: 'company',
        key: 'ui.assessment.recommendations',
    }),
    createSimpleFeature({
        id: 'company-assessment-manual-schedule',
        label: 'Manual assessment scheduling',
        description: 'Allow managers to manually schedule assessments.',
        scope: 'company',
        key: 'ui.assessment_manual_schedule',
    }),
    createDefaultTrueFeature({
        id: 'company-assessments-enabled',
        label: 'Assessments module',
        description: 'Keep the assessments module available in LMS.',
        scope: 'company',
        key: 'ui.assessments',
    }),
    createSimpleFeature({
        id: 'company-always-show-assessments',
        label: 'Always show assessments',
        description: 'Force the assessments tab to show even without subscription entitlements.',
        scope: 'company',
        key: 'ui.always_show_assessments',
    }),
    createSimpleFeature({
        id: 'company-trainings-charts',
        label: 'Training metrics charts',
        description: 'Display training progress charts inside automations.',
        scope: 'company',
        key: 'ui.trainings.charts',
    }),
    createSimpleFeature({
        id: 'company-trainings-due',
        label: 'Training due dates',
        description: 'Unlock due-date configuration in the training automation wizard.',
        scope: 'company',
        key: 'ui.trainings.due',
    }),
    createSimpleFeature({
        id: 'company-cmi5',
        label: 'cmi5 content support',
        description: 'Allow uploading and launching cmi5 learning packages.',
        scope: 'company',
        key: 'ui.cmi5.enabled',
    }),
    createDefaultTrueFeature({
        id: 'company-certificates-enabled',
        label: 'Certificates',
        description: 'Allow managers to preview and issue completion certificates.',
        scope: 'company',
        key: 'ui.certificates_enabled',
    }),
];

const ADMIN_FEATURES = [
    createSimpleFeature({
        id: 'company-api-keys',
        label: 'Hide API Keys',
        description: 'Hide the API Keys section under Settings.',
        scope: 'company',
        key: 'ui.api_keys_hidden',
        defaultState: false,
        enableValue: '1',
        disableValue: '0',
    }),
    createSimpleFeature({
        id: 'company-webhooks',
        label: 'Hide Webhooks',
        description: 'Hide the Webhooks section under Settings.',
        scope: 'company',
        key: 'ui.webhooks_hidden',
        defaultState: false,
        enableValue: '1',
        disableValue: '0',
    }),
    createSimpleFeature({
        id: 'partner-webhooks',
        label: 'Hide Partner Webhooks',
        description:
            'Hide the Webhooks section under partner Settings. Partner-scoped; independent of the company Webhooks toggle.',
        scope: 'partner',
        key: 'ui.partner_webhooks_hidden',
        defaultState: false,
        enableValue: '1',
        disableValue: '0',
    }),
    createSimpleFeature({
        id: 'nested-partners',
        label: 'Nested partners (distributors)',
        description:
            'Allow this partner to create sub-partners (distributors) that manage their own companies under the parent billing relationship.',
        scope: 'partner',
        key: 'nestedPartners.enabled',
    }),
    createSimpleFeature({
        id: 'company-audit-log',
        label: 'Audit log',
        description: 'Make the Audit Log section visible in Settings.',
        scope: 'company',
        key: 'ui.show_audit',
    }),
    createSimpleFeature({
        id: 'company-iframe-mode',
        label: 'Allow LMS iframe embedding',
        description: 'Permit the LMS to load within an iframe for partner embeds.',
        scope: 'company',
        key: 'ui.iframeMode.allowed',
    }),
];

const MY_PORTAL_FEATURES = [
    createSimpleFeature({
        id: 'my-feedback',
        label: 'Feedback link in MY',
        description: 'Show the feedback option in the MY user menu.',
        scope: 'company',
        key: 'ui.feedback_enabled',
    }),
    createInverseFeature({
        id: 'my-certificate-name-change',
        label: 'Allow certificate name changes',
        description: 'Let learners edit their name before downloading certificates.',
        scope: 'company',
        key: 'ui.certificate_name_change_disabled',
        defaultState: true,
    }),
];

export const FEATURE_SECTIONS = [
    { id: 'branding-content', label: 'Branding & Content', features: BRANDING_CONTENT_FEATURES },
    { id: 'learner-experience', label: 'Learner Experience', features: LEARNER_EXPERIENCE_FEATURES },
    { id: 'navigation', label: 'Navigation', features: NAVIGATION_FEATURES },
    { id: 'training-assessment', label: 'Training & Assessments', features: TRAINING_ASSESSMENT_FEATURES },
    { id: 'admin', label: 'Admin & Integrations', features: ADMIN_FEATURES },
    { id: 'my-portal', label: 'MY Portal', features: MY_PORTAL_FEATURES },
];

export const ALL_FEATURES = FEATURE_SECTIONS.flatMap((section) => section.features);
export const FEATURES_BY_ID = new Map(ALL_FEATURES.map((feature) => [feature.id, feature]));
export const FEATURE_SETTING_NAMES = new Set(
    ALL_FEATURES.flatMap((feature) => feature.relatedKeys)
);

export const getFeatureSettingNames = () =>
    Array.from(
        new Set(ALL_FEATURES.flatMap((feature) => feature.relatedKeys))
    );
